import { useEffect, useRef, useState } from 'preact/hooks'
import { userRolesApi } from '../../api/userRoles'
import type { AllCustomersAccessChangeDto, AllCustomersAccessDto } from '../../api/userRoles'
import type { RoleType } from '../../types/userRoles'
import { getDisplayErrorMessages } from '../../utils/errorHandling'

export interface AllCustomersAccessTarget {
  /** The managing company whose Users page this is. The route names it, so only its admins are answered. */
  managingCompanyId: string
  managingCompanyName: string
  userProfileId: string
}

/**
 * ORB-1019: one person's "Access to all customers" switch, as Edit roles shows it. Every change is
 * previewed by the server, customer by customer, through the same routine the save uses, so what the
 * panel says before Save is what Save does.
 */
export function useAllCustomersAccess(target: AllCustomersAccessTarget | null, isOpen: boolean) {
  const [current, setCurrent] = useState<AllCustomersAccessDto | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [role, setRole] = useState<RoleType>('Admin')
  const [preview, setPreview] = useState<AllCustomersAccessChangeDto | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewAttempt, setPreviewAttempt] = useState(0)
  const previewRequest = useRef(0)

  const managingCompanyId = target?.managingCompanyId
  const userProfileId = target?.userProfileId

  useEffect(() => {
    setCurrent(null)
    setLoadError(null)
    setPreview(null)
    if (!isOpen || !managingCompanyId || !userProfileId) return

    let cancelled = false
    userRolesApi.getAllCustomersAccess(managingCompanyId, userProfileId).then(response => {
      if (cancelled) return
      if (response.error || !response.data) {
        setLoadError(getDisplayErrorMessages(response.error ?? '', 'Access to all customers could not be read.').join(' '))
        return
      }
      setCurrent(response.data)
      setEnabled(response.data.Enabled)
      setRole(response.data.Role ?? 'Admin')
    })
    return () => { cancelled = true }
  }, [isOpen, managingCompanyId, userProfileId])

  const dirty = current !== null && (enabled !== current.Enabled || (enabled && role !== current.Role))

  useEffect(() => {
    setPreview(null)
    setPreviewError(null)
    if (!dirty || !managingCompanyId || !userProfileId) return

    // Only the newest answer lands: toggling twice quickly must not leave the first answer on screen.
    const request = ++previewRequest.current
    userRolesApi.getAllCustomersAccess(managingCompanyId, userProfileId, { Enabled: enabled, Role: role })
      .then(response => {
        if (request !== previewRequest.current) return
        if (response.error || !response.data?.Preview) {
          setPreviewError(getDisplayErrorMessages(response.error ?? '', 'What this changes could not be worked out.').join(' '))
          return
        }
        setPreview(response.data.Preview)
      })
  }, [dirty, enabled, role, managingCompanyId, userProfileId, previewAttempt])

  const retryPreview = () => setPreviewAttempt(attempt => attempt + 1)

  /** Saves the switch. Resolves with what changed, or rejects with the reason in words. */
  const save = async (): Promise<AllCustomersAccessChangeDto> => {
    const response = await userRolesApi.setAllCustomersAccess(managingCompanyId!, userProfileId!, { Enabled: enabled, Role: enabled ? role : null })
    if (response.error || !response.data) {
      throw new Error(getDisplayErrorMessages(response.error ?? '', 'Access to all customers could not be saved.').join(' '))
    }
    return response.data
  }

  // The effect above clears the preview only after the render that changed the selection, so for that one
  // render the old preview is still held. It must not be shown, or count as previewed, for the new selection.
  const previewIsCurrent = preview !== null && preview.Enabled === enabled && (!enabled || preview.Role === role)

  return {
    current, loadError, enabled, setEnabled, role, setRole,
    preview: previewIsCurrent ? preview : null,
    previewError, retryPreview, dirty, save,
  }
}
